/**
 * Generate a downloadable PNG token creation card from token data
 * Uses the template image from public folder as base
 */

export interface TokenCardData {
  logo_url: string | null;
  symbol: string;
  description: string | null;
  deployer_address: string;
  created_at: string;
  pool_address: string | null;
  nft_token_id: string | null;
  zns_domain?: string | null;  // Optional ZNS domain name
}

const TEMPLATE_IMAGE_PATH = '/token-creation-card-template2.png';

/**
 * Load an image from a URL and return as Image element
 */
const loadImage = (url: string): Promise<HTMLImageElement> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      console.log('[TokenCard] Image loaded successfully:', url);
      resolve(img);
    };
    img.onerror = (error) => {
      console.error('[TokenCard] Failed to load image:', url, error);
      reject(error);
    };
    // Properly encode the URL
    img.src = encodeURI(url);
  });
};

/**
 * Load a font file
 */
const loadFont = (family: string, url: string, weight: string): Promise<void> => {
  return new Promise((resolve) => {
    const font = new FontFace(family, `url(${url})`, { weight });
    font.load().then(() => {
      document.fonts.add(font);
      resolve();
    }).catch(() => resolve()); // Continue even if font fails
  });
};

/**
 * Format date to readable format
 */
const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    timeZoneName: 'short'
  });
};

/**
 * Format address with truncation (0x...last4)
 */
const formatAddress = (address: string): string => {
  if (!address) return 'N/A';
  const addr = address.startsWith('0x') ? address : `0x${address}`;
  if (addr.length <= 10) return addr; // Too short to truncate
  return `0x...${addr.slice(-4)}`;
};

/**
 * Generate a single frame of the token card with shimmer at specific position
 */
const generateCardFrame = async (
  tokenData: TokenCardData,
  shimmerPosition: number,
  ctx: CanvasRenderingContext2D
): Promise<void> => {
  // Clear canvas
  ctx.clearRect(0, 0, 683, 1024);

  // Load template
  try {
    const template = await loadImage(TEMPLATE_IMAGE_PATH);
    ctx.drawImage(template, 0, 0, 683, 1024);
  } catch (e) {
    drawGradientBackground(ctx, 683, 1024);
  }

  // Draw logo
  if (tokenData.logo_url) {
    try {
      const logo = await loadImage(tokenData.logo_url);
      const logoSize = 500;
      const logoX = (683 - logoSize) / 2;
      const logoY = (1024 - logoSize) / 2 - 100;
      
      ctx.drawImage(logo, 0, 0, logo.width, logo.height, logoX, logoY, logoSize, logoSize);
    } catch (e) {
      console.error('Failed to load logo:', e);
    }
  }

  // Draw border
  const borderMargin = 20;
  const cornerRadius = 30;
  const borderWidth = 8;
  
  ctx.save();
  
  ctx.beginPath();
  ctx.moveTo(borderMargin + cornerRadius, borderMargin);
  ctx.lineTo(683 - borderMargin - cornerRadius, borderMargin);
  ctx.quadraticCurveTo(683 - borderMargin, borderMargin, 683 - borderMargin, borderMargin + cornerRadius);
  ctx.lineTo(683 - borderMargin, 1024 - borderMargin - cornerRadius);
  ctx.quadraticCurveTo(683 - borderMargin, 1024 - borderMargin, 683 - borderMargin - cornerRadius, 1024 - borderMargin);
  ctx.lineTo(borderMargin + cornerRadius, 1024 - borderMargin);
  ctx.quadraticCurveTo(borderMargin, 1024 - borderMargin, borderMargin, 1024 - borderMargin - cornerRadius);
  ctx.lineTo(borderMargin, borderMargin + cornerRadius);
  ctx.quadraticCurveTo(borderMargin, borderMargin, borderMargin + cornerRadius, borderMargin);
  ctx.closePath();
  ctx.clip();
  
  const glowLayers = [
    { width: borderWidth + 8, alpha: 0.15, color: '#ff00ff' },
    { width: borderWidth + 4, alpha: 0.25, color: '#9333ff' },
    { width: borderWidth, alpha: 0.6, color: '#d946ef' }
  ];
  
  glowLayers.forEach((layer, index) => {
    ctx.strokeStyle = layer.color;
    ctx.lineWidth = layer.width;
    ctx.globalAlpha = layer.alpha;
    ctx.shadowBlur = index === 0 ? 15 : 10;
    ctx.shadowColor = layer.color;
    
    ctx.beginPath();
    ctx.moveTo(borderMargin + cornerRadius, borderMargin);
    ctx.lineTo(683 - borderMargin - cornerRadius, borderMargin);
    ctx.quadraticCurveTo(683 - borderMargin, borderMargin, 683 - borderMargin, borderMargin + cornerRadius);
    ctx.lineTo(683 - borderMargin, 1024 - borderMargin - cornerRadius);
    ctx.quadraticCurveTo(683 - borderMargin, 1024 - borderMargin, 683 - borderMargin - cornerRadius, 1024 - borderMargin);
    ctx.lineTo(borderMargin + cornerRadius, 1024 - borderMargin);
    ctx.quadraticCurveTo(borderMargin, 1024 - borderMargin, borderMargin, 1024 - borderMargin - cornerRadius);
    ctx.lineTo(borderMargin, borderMargin + cornerRadius);
    ctx.quadraticCurveTo(borderMargin, borderMargin, borderMargin + cornerRadius, borderMargin);
    ctx.stroke();
  });
  
  ctx.restore();

  // Add shimmer effect with specific position
  drawHolographicEffect(ctx, 683, 1024, shimmerPosition);

  // Draw text
  ctx.font = '14pt DS-DIGI';
  ctx.fillStyle = 'white';
  ctx.textAlign = 'left';
  ctx.textBaseline = 'top';
  
  const startY = 1024 - 160;
  const lineHeight = 22;

  // Use ZNS domain if available, otherwise use truncated address
  const creatorDisplay = tokenData.zns_domain || formatAddress(tokenData.deployer_address);
  
  ctx.fillText(`$${tokenData.symbol}`, 50, startY);
  ctx.fillText(`Dev: ${creatorDisplay}`, 50, startY + lineHeight);
  ctx.fillText(`Pair: ${formatAddress(tokenData.pool_address || 'N/A')}`, 50, startY + (lineHeight * 2));
  ctx.fillText(`NFT ID: ${tokenData.nft_token_id || 'N/A'}`, 50, startY + (lineHeight * 3));
  ctx.fillText(`Created at: ${formatDate(tokenData.created_at)}`, 50, startY + (lineHeight * 4));
};

/**
 * Generate token creation card as static PNG with diagonal shimmer effect
 */
export const generateTokenCard = async (
  tokenData: TokenCardData,
  onProgress?: (frame: number, total: number) => void
): Promise<Blob> => {
  const canvas = document.createElement('canvas');
  canvas.width = 683;
  canvas.height = 1024;
  const ctx = canvas.getContext('2d');
  
  if (!ctx) {
    throw new Error('Canvas context not available');
  }

  // Enable image smoothing for better quality
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';

  try {
    // Load font
    await loadFont('DS-DIGI', '/landing-assets/DS-DIGI.TTF', 'normal');

    // Generate single frame with shimmer effect at middle position (0.5)
    await generateCardFrame(tokenData, 0.5, ctx);
    
    if (onProgress) {
      onProgress(1, 1);
    }

    // Return as PNG
    return new Promise((resolve, reject) => {
      canvas.toBlob((blob) => {
        if (blob) {
          resolve(blob);
        } else {
          reject(new Error('Failed to generate image'));
        }
      }, 'image/png');
    });

  } catch (error) {
    console.error('Error generating token card:', error);
    throw error;
  }
};

/**
 * Draw holographic shimmer/shine effect diagonal from top-left
 * Creates a light reflection that sweeps diagonally across the card
 */
const drawHolographicEffect = (ctx: CanvasRenderingContext2D, width: number, height: number, shimmerPosition: number = 0) => {
  // Create base holographic gradient across the entire card (diagonal)
  const gradient = ctx.createLinearGradient(0, 0, width, height);
  gradient.addColorStop(0, 'rgba(255, 0, 255, 0)');     // Pink transparent at top-left
  gradient.addColorStop(0.2, 'rgba(147, 51, 255, 0.08)'); // Purple glow
  gradient.addColorStop(0.5, 'rgba(255, 215, 0, 0.12)'); // Gold shine
  gradient.addColorStop(0.8, 'rgba(147, 51, 255, 0.08)'); // Purple glow
  gradient.addColorStop(1, 'rgba(0, 255, 255, 0)');     // Cyan transparent at bottom-right
  
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, width, height);
  
  // Add diagonal highlight band (light coming from top-left)
  ctx.save();
  ctx.globalAlpha = 0.4;
  
  // Calculate diagonal shimmer position
  // Moving from top-left (-400, -400) to bottom-right (width + 400, height + 400)
  const diagonalLength = Math.sqrt(width * width + height * height);
  const startX = -400 + (shimmerPosition * (width + 800));
  const startY = -400 + (shimmerPosition * (height + 800));
  
  // Create diagonal gradient for the shimmer band
  const endX = startX + 300;
  const endY = startY + 300;
  const highlightGradient = ctx.createLinearGradient(startX, startY, endX, endY);
  highlightGradient.addColorStop(0, 'rgba(255, 255, 255, 0)');
  highlightGradient.addColorStop(0.3, 'rgba(255, 255, 255, 0.3)');
  highlightGradient.addColorStop(0.5, 'rgba(255, 255, 255, 0.6)');
  highlightGradient.addColorStop(0.7, 'rgba(255, 255, 255, 0.3)');
  highlightGradient.addColorStop(1, 'rgba(255, 255, 255, 0)');
  
  ctx.fillStyle = highlightGradient;
  
  // Draw diagonal shimmer band
  // Rotate to align with diagonal
  ctx.save();
  const centerX = width / 2;
  const centerY = height / 2;
  ctx.translate(centerX, centerY);
  ctx.rotate(Math.atan2(height, width)); // Diagonal angle
  ctx.fillRect(-diagonalLength / 2, -100, diagonalLength, 200);
  ctx.restore();
  
  ctx.restore();
};


/**
 * Draw gradient background
 */
const drawGradientBackground = (ctx: CanvasRenderingContext2D, width: number, height: number) => {
  // Create gradient from dark purple to bright purple
  const gradient = ctx.createLinearGradient(0, 0, width, height);
  gradient.addColorStop(0, '#4A148C'); // Dark purple
  gradient.addColorStop(1, '#9D2DFF'); // Bright purple
  
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, width, height);
  
  // Add subtle texture/noise
  const imageData = ctx.getImageData(0, 0, width, height);
  const data = imageData.data;
  for (let i = 0; i < data.length; i += 4) {
    if (Math.random() < 0.05) {
      data[i] = Math.min(255, data[i] + 20);     // R
      data[i + 1] = Math.min(255, data[i + 1] + 20); // G
      data[i + 2] = Math.min(255, data[i + 2] + 20); // B
    }
  }
  ctx.putImageData(imageData, 0, 0);
};

/**
 * Download the token card as PNG
 */
export const downloadTokenCard = async (tokenData: TokenCardData, filename: string = 'token-card.png') => {
  try {
    const blob = await generateTokenCard(tokenData);
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Error downloading token card:', error);
    throw error;
  }
};

